/**
 * Portions of this file (combobox-related code) are adapted (copied and partially modified for this project) from the W3C WAI-ARIA Authoring Practices
 * combobox example:
 *
 * https://www.w3.org/WAI/content-assets/wai-aria-practices/patterns/combobox/examples/js/combobox-autocomplete.js
 *
 * Copyright © 2025 World Wide Web Consortium. All Rights Reserved. This work is distributed under the W3C® Software and Document License [1] in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * [1] https://www.w3.org/Consortium/Legal/copyright-software
 *
 * The W3C Software and Document License 2023 superseded the original W3C Software License 2015
 *
 * Full text of W3C NOTICE/License (2023):
 *
 * By obtaining and/or copying this work, you (the licensee) agree that you have read, understood, and will comply with the following terms and conditions.
 *
 * Permission to copy, modify, and distribute this work, with or without modification, for any purpose and without fee or royalty is hereby granted, provided that you include the following on ALL copies of the work or portions thereof, including modifications:
 *
 *  • The full text of this NOTICE in a location viewable to users of the redistributed or derivative work.
 *  • Any pre-existing intellectual property disclaimers, notices, or terms and conditions. If none exist, the W3C software and document short notice should be included.
 *  • Notice of any changes or modifications, through a copyright statement on the new code or document such as "This software or document includes material copied from or derived from [title and URI of the W3C document]. Copyright © [$year-of-document] World Wide Web Consortium. https://www.w3.org/copyright/software-license-2023/"
 *
 * THIS WORK IS PROVIDED "AS IS," AND COPYRIGHT HOLDERS MAKE NO REPRESENTATIONS OR WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO, WARRANTIES OF MERCHANTABILITY OR FITNESS FOR ANY PARTICULAR PURPOSE OR THAT THE USE OF THE SOFTWARE OR DOCUMENT WILL NOT INFRINGE ANY THIRD PARTY PATENTS, COPYRIGHTS, TRADEMARKS OR OTHER RIGHTS.
 *
 * COPYRIGHT HOLDERS WILL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, SPECIAL OR CONSEQUENTIAL DAMAGES ARISING OUT OF ANY USE OF THE SOFTWARE OR DOCUMENT.
 *
 * The name and trademarks of copyright holders may NOT be used in advertising or publicity pertaining to the work without specific, written prior permission. Title to copyright in this work will at all times remain with copyright holders.
 */

import React, {useEffect, useState, useCallback} from "react";
import axios from "axios";
import {useTranslation} from "react-i18next";
import Cookies from "universal-cookie";
import {searchURL, suggestURL} from "./helper";
import Configuration from "../../../config";
import getMenuConfig from "../../../helpers/menuConfig";
import {ServiceLinkWithChildren} from "../../../helpers/helpers";
import {useDispatch} from "react-redux";
import {redirectUrlSuggestion} from "../../../reducers/app";
import {useHistory} from "react-router-dom";

const SearchModal = (props) => {
  const cookies = new Cookies();
  let language = cookies.get("language");

  const {t} = useTranslation();

  const history = useHistory();

  const removeSuggestionSearch = (classCombo) => {
      var suggestionsSearch = document.querySelectorAll("." + classCombo + " .serp-chips");
      suggestionsSearch.forEach(function(option) {
          option.remove();
      });
  }

  class ComboboxAutocomplete {
    constructor(comboboxNode, listboxNode, classCombo) {
      this.comboboxNode = comboboxNode;
      this.listboxNode = listboxNode;
        this.classCombo = classCombo;

      this.comboboxHasVisualFocus = false;
      this.listboxHasVisualFocus = false;

      this.hasHover = false;

      this.isNone = false;
      this.isList = false;
      this.isBoth = false;
      this.option = null;
      this.firstOption = null;
      this.lastOption = null;

      this.filteredOptions = [];
      this.filter = '';

      var autocomplete = this.comboboxNode.getAttribute('aria-autocomplete');

      if (typeof autocomplete === 'string') {
        autocomplete = autocomplete.toLowerCase();
        this.isNone = autocomplete === 'none';
        this.isList = autocomplete === 'list';
        this.isBoth = autocomplete === 'both';
      } else {
        this.isNone = true;
      }

      this.comboboxNode.addEventListener(
        'keydown',
        this.onComboboxKeyDown.bind(this)
      );
      this.comboboxNode.addEventListener(
        'keyup',
        this.onComboboxKeyUp.bind(this)
      );
      this.comboboxNode.addEventListener(
        'click',
        this.onComboboxClick.bind(this)
      );
      this.comboboxNode.addEventListener(
        'focus',
        this.onComboboxFocus.bind(this)
      );
      this.comboboxNode.addEventListener('blur', this.onComboboxBlur.bind(this));

      document.body.addEventListener(
        'pointerup',
        this.onBackgroundPointerUp.bind(this),
        true
      );

      // initialize pop up menu

      this.listboxNode.addEventListener(
        'pointerover',
        this.onListboxPointerover.bind(this)
      );
      this.listboxNode.addEventListener(
        'pointerout',
        this.onListboxPointerout.bind(this)
      );

      this.filterOptions();
    }

    getLowercaseContent(node) {
      return node.textContent.toLowerCase();
    }

    isOptionInView(option) {
      var bounding = option.getBoundingClientRect();
      return (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    }

    setActiveDescendant(option) {
      if (option && this.listboxHasVisualFocus) {
        this.comboboxNode.setAttribute('aria-activedescendant', option.id);
        if (!this.isOptionInView(option)) {
          option.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } else {
        this.comboboxNode.setAttribute('aria-activedescendant', '');
      }
    }

    setValue(value) {
      this.filter = value;
      this.comboboxNode.value = this.filter;
      this.comboboxNode.setSelectionRange(this.filter.length, this.filter.length);
      this.filterOptions();
    }

    setOption(option, flag) {
      if (typeof flag !== 'boolean') {
        flag = false;
      }

      if (option) {
        this.option = option;
        this.setCurrentOptionStyle(this.option);
        this.setActiveDescendant(this.option);

        if (this.isBoth) {
          this.comboboxNode.value = this.option.textContent;
          if (flag) {
            this.comboboxNode.setSelectionRange(
              this.option.textContent.length,
              this.option.textContent.length
            );
          } else {
            this.comboboxNode.setSelectionRange(
              this.filter.length,
              this.option.textContent.length
            );
          }
        }
      }
    }

    setVisualFocusCombobox() {
      this.listboxNode.classList.remove('focus');
      this.comboboxNode.parentNode.classList.add('focus');
      this.comboboxHasVisualFocus = true;
      this.listboxHasVisualFocus = false;
      this.setActiveDescendant(false);
    }

    setVisualFocusListbox() {
      this.comboboxNode.parentNode.classList.remove('focus');
      this.comboboxHasVisualFocus = false;
      this.listboxHasVisualFocus = true;
      this.listboxNode.classList.add('focus');
      this.setActiveDescendant(this.option);
    }

    removeVisualFocusAll() {
      this.comboboxNode.parentNode.classList.remove('focus');
      this.comboboxHasVisualFocus = false;
      this.listboxHasVisualFocus = false;
      this.listboxNode.classList.remove('focus');
      this.option = null;
      this.setActiveDescendant(false);
    }

    // ComboboxAutocomplete Events

    filterOptions() {
      if (this.isNone) {
        this.filter = '';
      }

      var option = null;
      var currentOption = this.option;
      var filter = this.filter.toLowerCase();

      this.filteredOptions = [];
      this.listboxNode.innerHTML = '';

      if (filter.length >= 3) {
          console.log("3 characters");
          searchSuggestion(this,filter,option,currentOption,this.classCombo);
      }
    }

    setCurrentOptionStyle(option) {
      for (var i = 0; i < this.filteredOptions.length; i++) {
        var opt = this.filteredOptions[i];
        if (opt === option) {
          opt.setAttribute('aria-selected', 'true');
          if (
            this.listboxNode.scrollTop + this.listboxNode.offsetHeight <
            opt.offsetTop + opt.offsetHeight
          ) {
            this.listboxNode.scrollTop =
              opt.offsetTop + opt.offsetHeight - this.listboxNode.offsetHeight;
          } else if (this.listboxNode.scrollTop > opt.offsetTop + 2) {
            this.listboxNode.scrollTop = opt.offsetTop;
          }
        } else {
          opt.removeAttribute('aria-selected');
        }
      }
    }

    getPreviousOption(currentOption) {
      if (currentOption !== this.firstOption) {
        var index = this.filteredOptions.indexOf(currentOption);
        return this.filteredOptions[index - 1];
      }
      return this.lastOption;
    }

    getNextOption(currentOption) {
      if (currentOption !== this.lastOption) {
        var index = this.filteredOptions.indexOf(currentOption);
        return this.filteredOptions[index + 1];
      }
      return this.firstOption;
    }

    /* MENU DISPLAY METHODS */

    doesOptionHaveFocus() {
      return this.comboboxNode.getAttribute('aria-activedescendant') !== '';
    }

    isOpen() {
      return this.listboxNode.style.display === 'block';
    }

    isClosed() {
      return this.listboxNode.style.display !== 'block';
    }

    hasOptions() {
      return this.filteredOptions.length;
    }

    open() {
      this.listboxNode.style.display = 'block';
      this.comboboxNode.setAttribute('aria-expanded', 'true');
      this.comboboxNode.closest(".input-group").classList.add("with-options");
    }

    close(force) {
      if (typeof force !== 'boolean') {
        force = false;
      }

      if (
        force ||
        (!this.comboboxHasVisualFocus &&
          !this.listboxHasVisualFocus &&
          !this.hasHover)
      ) {
        this.setCurrentOptionStyle(false);
        this.listboxNode.style.display = 'none';
        this.comboboxNode.setAttribute('aria-expanded', 'false');
        this.comboboxNode.closest(".input-group").classList.remove("with-options");
        this.setActiveDescendant(false);
        this.comboboxNode.parentNode.classList.add('focus');
      }
    }

    /* combobox Events */

    onComboboxKeyDown(event) {
      var flag = false,
        altKey = event.altKey;

      if (event.ctrlKey || event.shiftKey) {
        return;
      }

      switch (event.key) {
        case 'Enter':
          if (this.listboxHasVisualFocus) {
            this.setValue(this.option.textContent);
            this.option.querySelector("a").click();
          } else {
            this.close(true);
            this.setVisualFocusCombobox();
            flag = true;
            searchInputText(this.comboboxNode.value);
          }
          break;

        case 'Down':
        case 'ArrowDown':
          if (this.filteredOptions.length > 0) {
            if (altKey) {
              this.open();
            } else {
              this.open();
              if (
                this.listboxHasVisualFocus ||
                (this.isBoth && this.filteredOptions.length > 1)
              ) {
                this.setOption(this.getNextOption(this.option), true);
                this.setVisualFocusListbox();
              } else {
                this.setOption(this.firstOption, true);
                this.setVisualFocusListbox();
              }
            }
          }
          flag = true;
          break;

        case 'Up':
        case 'ArrowUp':
          if (this.hasOptions()) {
            if (this.listboxHasVisualFocus) {
              this.setOption(this.getPreviousOption(this.option), true);
            } else {
              this.open();
              if (!altKey) {
                this.setOption(this.lastOption, true);
                this.setVisualFocusListbox();
              }
            }
          }
          flag = true;
          break;

        case 'Esc':
        case 'Escape':
          if (this.isOpen()) {
            this.close(true);
            this.filter = this.comboboxNode.value;
            this.filterOptions();
            this.setVisualFocusCombobox();
          } else {
            this.setValue('');
            this.comboboxNode.value = '';
          }
          this.option = null;
          flag = true;
          break;

        case 'Tab':
          this.close(true);
          if (this.listboxHasVisualFocus) {
            if (this.option) {
              this.setValue(this.option.textContent);
            }
          }
          break;

        case 'Home':
          this.comboboxNode.setSelectionRange(0, 0);
          flag = true;
          break;

        case 'End':
          var length = this.comboboxNode.value.length;
          this.comboboxNode.setSelectionRange(length, length);
          flag = true;
          break;

        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    isPrintableCharacter(str) {
      return str.length === 1 && str.match(/\S| /);
    }

    onComboboxKeyUp(event) {
      var flag = false,
        option = null,
        char = event.key;

      if (!event.key) {
          this.filter = event.target.value;
      } else {
          if (this.isPrintableCharacter(char)) {
            this.filter += char;
          }
      }

      if (this.comboboxNode.value.length < this.filter.length) {
        this.filter = this.comboboxNode.value;
        this.option = null;
        this.filterOptions();
      }

      if (event.key === 'Escape' || event.key === 'Esc') {
        return;
      }

      switch (event.key) {
        case 'Backspace':
          this.setVisualFocusCombobox();
          this.setCurrentOptionStyle(false);
          this.filter = this.comboboxNode.value;
          this.option = null;
          this.filterOptions();
          if(!this.hasOptions()) {
              this.close(true);
          }
          flag = true;
          break;

        case 'Left':
        case 'ArrowLeft':
        case 'Right':
        case 'ArrowRight':
        case 'Home':
        case 'End':
          if (this.isBoth) {
            this.filter = this.comboboxNode.value;
          } else {
            this.option = null;
            this.setCurrentOptionStyle(false);
          }
          this.setVisualFocusCombobox();
          flag = true;
          break;

        default:
          if (!event.key || this.isPrintableCharacter(char)) {
            this.setVisualFocusCombobox();
            this.setCurrentOptionStyle(false);
            flag = true;

            if (this.isList || this.isBoth) {
              option = this.filterOptions();
              if (option) {
                if (this.isClosed() && this.comboboxNode.value.length) {
                  this.open();
                }

                if (
                  this.getLowercaseContent(option).indexOf(
                    this.comboboxNode.value.toLowerCase()
                  ) === 0
                ) {
                  this.option = option;
                  if (this.isBoth || this.listboxHasVisualFocus) {
                    this.setCurrentOptionStyle(option);
                    if (this.isBoth) {
                      this.setOption(option);
                    }
                  }
                } else {
                  this.option = null;
                  this.setCurrentOptionStyle(false);
                }
              } else {
                this.close();
                this.comboboxNode.closest(".input-group").classList.remove("with-options");
                this.option = null;
                this.setActiveDescendant(false);
              }
            } else if (this.comboboxNode.value.length) {
              this.open();
            }
          }

          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    onComboboxClick() {
      if (this.isOpen()) {
        this.close(true);
      } else if (this.listboxNode.innerHTML !== '') {
        this.open();
      }
    }

    onComboboxFocus() {
      this.filter = this.comboboxNode.value;
      this.filterOptions();
      this.setVisualFocusCombobox();
      this.option = null;
      this.setCurrentOptionStyle(null);
    }

    onComboboxBlur() {
      this.removeVisualFocusAll();
    }

    onBackgroundPointerUp(event) {
      if (
        !this.comboboxNode.contains(event.target) &&
        !this.listboxNode.contains(event.target)
      ) {
        this.comboboxHasVisualFocus = false;
        this.setCurrentOptionStyle(null);
        this.removeVisualFocusAll();
        setTimeout(this.close.bind(this, true), 300);
      }
    }

    /* Listbox Events */

    onListboxPointerover() {
      this.hasHover = true;
    }

    onListboxPointerout() {
      this.hasHover = false;
      setTimeout(this.close.bind(this, false), 300);
    }

    // Listbox Option Events

    onOptionClick(event) {
      this.close(true);
    }

    onOptionPointerover() {
      this.hasHover = true;
      this.open();
    }

    onOptionPointerout() {
      this.hasHover = false;
      setTimeout(this.close.bind(this, false), 300);
    }
  }

  const searchSuggestion = useCallback(async (combobox, keyupValue, option, currentOption, classCombo) => {
    axios
      .get(suggestURL(Configuration.get("REACT_APP_GCAP_PUBLIC_HOST"),language, keyupValue))
      .then((response) => {
        let arrayElements = [];
        const data = response.data;
        if (data && data.message && data.message.elements) {
          removeSuggestionSearch(classCombo);
          let elementsFound = data.message.elements;
          if (elementsFound.length > 0) {
            elementsFound.map((textFound) => {
              if (elementsFound.length > 0) {
                if(arrayElements.length < 3) {
                  var option = document.createElement("li");
                  option.classList.add("dropdown-item");
                  option.classList.add("serp-chips");
                  option.setAttribute("role" , "option");

                  option.innerHTML = "<a href=" + textFound.public_location + " class='gcap-cta'><span>" + textFound.title + "</span><i class='gc-icon-functional-arrow-right' aria-hidden='true'></i></a>";
                  document.querySelector(".widget-search__suggestion").append(option);
                  return arrayElements.push({
                    title: textFound.title,
                    link: textFound.public_location,
                  });
                }
              }
            });

            var nodes = combobox.listboxNode.getElementsByTagName('LI');
              for (var i = 0; i < nodes.length; i++) {
              var node = nodes[i];
              combobox.filteredOptions.push(node);

              node.addEventListener('click', combobox.onOptionClick());
              node.addEventListener('pointerover', combobox.onOptionPointerover());
              node.addEventListener('pointerout', combobox.onOptionPointerout());
            }

            var numItems = combobox.filteredOptions.length;

            if (numItems > 0) {
              combobox.firstOption = combobox.filteredOptions[0];
              combobox.lastOption = combobox.filteredOptions[numItems - 1];

              if (currentOption && combobox.filteredOptions.indexOf(currentOption) >= 0) {
               option = currentOption;
              } else {
               option = combobox.firstOption;
              }
            } else {
              combobox.firstOption = null;
              option = null;
              combobox.lastOption = null;
            }
            return option;
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [language]);


  const searchInputText = useCallback(async (value) => {
    dispatch(redirectUrlSuggestion(searchURL(Configuration.get("REACT_APP_GCAP_PUBLIC_HOST"), language, value)))
    history.push(`/redirect/search`);
    // eslint-disable-next-line
  }, [language]);

  const dispatch = useDispatch();

  useEffect(() => {
    var inputPageSearch = document.querySelectorAll("#ricerca");
    if (inputPageSearch.length > 0) {
        document.querySelector("#ricerca-1").val(inputPageSearch.val());
    }
    var comboboxes = document.querySelectorAll('.combobox-list');

    for (var i = 0; i < comboboxes.length; i++) {
        var combobox = comboboxes[i];
        var comboboxNode = combobox.querySelector('input');
        var classCombo = "combobox-suggestion"+ i;
        combobox.querySelector(".widget-search__suggestion").classList.add(classCombo);
        var listboxNode = combobox.querySelector('[role="listbox"]');
        new ComboboxAutocomplete(comboboxNode, listboxNode, classCombo);
    }

    document.addEventListener('keydown', function(e) {
      if (e.keyCode === 9) {
        document.querySelector('body').classList.add('show-tab-focus');
      }
    });

    document.addEventListener('click', function(e) {
      document.querySelector('body').classList.remove('show-tab-focus');
    });
  },[]);

  return (
    <div
      className="SearchModal modal gcap-modal search-modal fade show full-page-overlay"
      id="searchModal"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="searchModalTitle"
      aria-hidden="true"
      data-testid="SearchModal"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="container">
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={props.close}
              data-testid="Close-button"
            >
              <span className="gc-icon-functional-close" aria-hidden="true"/>
            </button>
          </div>
          <div className="modal-body">
            <div className="container">
              <div className="row">
                <div id="searchModalTitle" className="search-modal-title headline-bold-fs3 font-color-1">{t("header.menu.search.title")}</div>
                <div className="widget-search js-initWidgetSearch dropdown combobox-list">
                    <div className="input-group-container">
                        <div className="input-group">
                            <div className="input-group-append">
                                <i className="gc-icon-functional-search-1-col" aria-hidden="true"></i>
                            </div>
                            <label htmlFor="ricerca-1" className="sr-only">
                              {t("header.menu.search")}
                            </label>
                            <input id="ricerca-1" type="text" className="form-control" aria-describedby="ricerca-1" placeholder={t("header.menu.search.placeholder")} aria-controls="search-suggestions" role="combobox" aria-expanded="false" aria-autocomplete="list" data-testid="Ricerca-1"/>
                        </div>
                    </div>
                    <ul className="widget-search__suggestion dropdown-menu" id="search-suggestions" role="listbox"></ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
